import { APIResponseType } from 'app/apiService/types';
import apiService from '..';

export interface OnboardingChecklistItem {
  id: string;
  label: string;
  validated: boolean;
}

export interface OnboardingStepResourceLink {
  id: string;
  label: string;
  url: string;
  guidance: string;
}

export interface OnboardingStepValidationResult {
  step_id: string;
  can_complete: boolean;
  checklist: OnboardingChecklistItem[];
  resource_links?: OnboardingStepResourceLink[];
}

export interface OnboardingStepAccessibility {
  accessible: boolean;
  blocked_by: string[];
}

export type OnboardingStepAccessibilityMap = Record<string, OnboardingStepAccessibility>;

export interface DealerOnboardingProgress {
  dealer_id: string;
  tour_id: string;
  status: 'active' | 'completed' | 'ended';
  current_step_index: number;
  step_completed: Record<string, boolean>;
  step_accessibility?: Record<string, OnboardingStepAccessibility>;
  step_context?: Record<string, Record<string, unknown>>;
  last_viewed_step_id?: string;
  has_paused_session?: boolean;
  has_launch_readiness_intro_acknowledged?: boolean;
  completed_at?: string;
  updated_at?: string;
}

export interface SaveDealerOnboardingProgressPayload {
  status?: 'active' | 'completed' | 'ended';
  current_step_index?: number;
  step_completed?: Record<string, boolean>;
  step_context?: Record<string, Record<string, unknown>>;
  last_viewed_step_id?: string;
  has_paused_session?: boolean;
  has_launch_readiness_intro_acknowledged?: boolean;
  reset?: boolean;
}

class DealerOnboardingApiService {
  static ENDPOINT = 'api/v2/dealer-onboarding';

  static async getProgress(dealerId: string): Promise<DealerOnboardingProgress> {
    if (!dealerId) {
      throw new Error('Dealer ID is required to fetch onboarding progress');
    }

    const response = await apiService.get<APIResponseType<DealerOnboardingProgress>>(
      `${this.ENDPOINT}?dealer_id=${dealerId}`,
    );
    return response.body;
  }

  static async saveProgress(
    dealerId: string,
    payload: SaveDealerOnboardingProgressPayload,
  ): Promise<DealerOnboardingProgress> {
    if (!dealerId) {
      throw new Error('Dealer ID is required to save onboarding progress');
    }

    const response = await apiService.put<APIResponseType<DealerOnboardingProgress>>(
      `${this.ENDPOINT}?dealer_id=${dealerId}`,
      payload,
    );
    await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
    return response.body;
  }

  static async getStepValidation(
    dealerId: string,
    stepId: string,
    options?: {
      crmSupportsEmailReports?: boolean;
      sandboxReadinessRequired?: boolean;
    },
  ): Promise<OnboardingStepValidationResult> {
    if (!dealerId) {
      throw new Error('Dealer ID is required to validate onboarding step');
    }
    if (!stepId) {
      throw new Error('Step ID is required to validate onboarding step');
    }

    const params = new URLSearchParams({ dealer_id: dealerId });
    if (typeof options?.crmSupportsEmailReports === 'boolean') {
      params.set('crm_supports_email_reports', String(options.crmSupportsEmailReports));
    }
    if (typeof options?.sandboxReadinessRequired === 'boolean') {
      params.set('sandbox_readiness_required', String(options.sandboxReadinessRequired));
    }

    const response = await apiService.get<APIResponseType<OnboardingStepValidationResult>>(
      `${this.ENDPOINT}/steps/${encodeURIComponent(stepId)}/validation?${params.toString()}`,
    );
    return response.body;
  }

  static async completeStep(
    dealerId: string,
    stepId: string,
    currentStepIndex?: number,
  ): Promise<DealerOnboardingProgress> {
    if (!dealerId) {
      throw new Error('Dealer ID is required to complete onboarding step');
    }
    if (!stepId) {
      throw new Error('Step ID is required to complete onboarding step');
    }

    const response = await apiService.post<APIResponseType<DealerOnboardingProgress>>(
      `${this.ENDPOINT}/steps/${encodeURIComponent(stepId)}/complete?dealer_id=${dealerId}`,
      typeof currentStepIndex === 'number' ? { current_step_index: currentStepIndex } : {},
    );
    await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
    return response.body;
  }
}

export default DealerOnboardingApiService;
